import { makeStateStore } from '@playcastdotio/StateStore';
import type { StateStore } from '@playcastdotio/CoreTypes';
import { generators } from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IReplicationRouter } from '../../ReplicationRouter';
import { USE_STREAM_ARCHITECTURE } from '../../../config/featureFlags';
import type { StreamCoordinator } from '../../stream/StreamCoordinator';

export type DisplayInfo = {
  displayId: number;
  width: number;
  height: number;
  x: number;
  y: number;
};

export type SourceProperties = {
  sourceId: number;
  adapterId: number;
  desktopCoordinates: { left: number; top: number; right: number; bottom: number };
  virtualCoordinates: { left: number; top: number; right: number; bottom: number };
};

export type DisplayOutputState = {
  displays: DisplayInfo[];
  selectedDisplayId: number;
  sourceProperties: SourceProperties[];
  isLive: boolean;
  startTime: number;
};

export type DisplayOutputDeps = {
  ipcService: IIpcService;
  getActiveShadowcams: () => string[];
  // Owns the host's view of Playjector's stream world. Required when the stream architecture is
  // enabled so a display pick can be resolved to a frame-output RegistryId and pushed to the base
  // stream. Optional so the legacy (flag-off) path can construct the manager without it.
  streamCoordinator?: StreamCoordinator;
};

export class DisplayOutputManager {
  private store: StateStore<DisplayOutputState>;
  private deps: DisplayOutputDeps;

  constructor(deps: DisplayOutputDeps) {
    this.deps = deps;
    this.store = makeStateStore<DisplayOutputState>({
      displays: [],
      selectedDisplayId: 0,
      sourceProperties: [],
      isLive: false,
      startTime: 0,
    });
  }

  public getStore(): StateStore<DisplayOutputState> {
    return this.store;
  }

  public registerReplicationHandler(router: IReplicationRouter): void {
    router.register('displayState', this.onDisplayStateReplication.bind(this));
    console.log('[DisplayOutputManager] Registered replication handler for displayState');
  }

  public changeDisplay(displayId: number): void {
    if (USE_STREAM_ARCHITECTURE) {
      // Retarget the base stream's frame output to the chosen display via the StreamCoordinator
      // (grabber sourceId === the picker's monitor-index sourceId). setPicking keeps follow-mouse
      // handled inside Playjector.
      const coordinator = this.deps.streamCoordinator;
      if (!coordinator) {
        console.error('[DisplayOutputManager] StreamCoordinator not injected');
        return;
      }
      if (coordinator.resolveFrameOutputForDisplay(displayId) === undefined) {
        console.error('[DisplayOutputManager] Stream frame output not found for displayId:', displayId);
        return;
      }
      if (!coordinator.getBaseStreamId()) {
        console.error('[DisplayOutputManager] Base stream id not known yet');
        return;
      }
      this.store.updateState({
        ...this.store.currentState,
        selectedDisplayId: displayId,
      });
      this.deps.ipcService.send(generators.camera.setPicking(true));
      coordinator.retargetBaseStreamToDisplay(displayId);
      return;
    }

    const props = this.store.currentState.sourceProperties.find(
      (s) => s.sourceId === displayId,
    );
    if (!props) {
      console.error('[DisplayOutputManager] Source properties not found for displayId:', displayId);
      return;
    }

    this.store.updateState({
      ...this.store.currentState,
      selectedDisplayId: displayId,
    });

    this.deps.ipcService.send(generators.camera.setPicking(true));

    const activeCams = this.deps.getActiveShadowcams();
    activeCams.forEach((cam) => {
      this.deps.ipcService.send(
        generators.camera.setSource(cam, {
          sourceId: props.sourceId,
          adapterId: props.adapterId,
          desktopCoordinates: props.desktopCoordinates,
          virtualCoordinates: props.virtualCoordinates,
        }),
      );
    });
  }

  public updateStreamStatus(isLive: boolean, startTime: number): void {
    this.store.updateState({
      ...this.store.currentState,
      isLive,
      startTime,
    });
  }

  private onDisplayStateReplication(state: unknown): void {
    if (!state || typeof state !== 'object') return;
    const display = state as Record<string, unknown>;

    const updates: Partial<DisplayOutputState> = {};

    if (Array.isArray(display.displays)) {
      updates.displays = display.displays as DisplayInfo[];
    }
    if (display.info && typeof display.info === 'object') {
      const info = display.info as Record<string, unknown>;
      if (typeof info.selectedDisplayId === 'number') {
        updates.selectedDisplayId = info.selectedDisplayId;
      }
    }
    if (Array.isArray(display.sourceProperties)) {
      updates.sourceProperties = display.sourceProperties as SourceProperties[];
    }

    if (Object.keys(updates).length > 0) {
      this.store.updateState({ ...this.store.currentState, ...updates });
    }
  }
}
